import React from 'react';

import Config from '@config';
import {
  ErrorView as Error,
  ISO_MONTH_FORMAT,
  NoPermissions,
  NotFound,
  PathBuilder,
  rolesChildren,
} from '@gasbi/shared';
import { OrganizationPages } from '@gasbi/shared/web/Organization/pages';
import {
  EmployeeDetails,
  MyOrganization,
  MyOrganizationBankDetails,
  MyOrganizationEmployees,
  MyOrganizationInformation,
} from '@gasbi/shared/web/RbacOrganization';
import dayjs from 'dayjs';
import { Navigate, Outlet, createBrowserRouter } from 'react-router-dom';

import { UserPermissions } from '@/features/permissions';
import { Pages } from '@/pages';
import { advanceInvoicesRoutes } from '@/pages/AdvanceInvoices/routes';
import { contractDetailsRoutes } from '@/pages/ContractDetails/routes';
import { counterpartyDetailRoutes } from '@/pages/CounterpartyDetails/routes';
import { gasTariffsDetailRoutes } from '@/pages/GasTariffs/routes';
import { resourceDetailRoutes } from '@/pages/ResourceDetails/routes';
import { resourcesDetailRoutes } from '@/pages/Resources/routes';
import {
  OrganizationApiContextWrapper,
  SupplyOrganizationApiWrapper,
} from '@/providers';
import type { AppRouteObject } from '@/routeTypes';

const Router = React.lazy(() => import('@/layout/Router'));
const AuthLanding = React.lazy(() => import('@/pages/auth/AuthCallback'));
const NotAuthorizedPage = React.lazy(
  () => import('@/pages/auth/NotAuthorizedPage'),
);

const UndistributedPayments = React.lazy(
  () => import('@/pages/UndistributedPayments'),
);
const PaymentImports = React.lazy(() => import('@/pages/PaymentImports'));
const PaymentImportDetails = React.lazy(
  () => import('@/pages/PaymentImportDetails'),
);
const Counterparties = React.lazy(() => import('@/pages/Counterparties'));
const CounterpartyDetails = React.lazy(
  () => import('@/pages/CounterpartyDetails'),
);

const GasTariffs = React.lazy(() => import('@/pages/GasTariffs'));
const Resources = React.lazy(() => import('@/pages/Resources'));
const ResourceDetails = React.lazy(() => import('@/pages/ResourceDetails'));
const IncomingPayments = React.lazy(() => import('@/pages/IncomingPayments'));
const IncomingPaymentDetails = React.lazy(
  () => import('@/pages/IncomingPaymentDetails'),
);
const Contracts = React.lazy(() => import('@/pages/Contracts'));
const AdvanceInvoices = React.lazy(() => import('@/pages/AdvanceInvoices'));
const ContractDetails = React.lazy(() => import('@/pages/ContractDetails'));
const Reports = React.lazy(() => import('@/pages/Reports'));
const ReportConstructor = React.lazy(() => import('@/pages/ReportConstructor'));
const Audit = React.lazy(() => import('@/pages/Audit'));
const BiDashboards = React.lazy(() => import('@/pages/BiDashboards'));
const ImbalanceSettings = React.lazy(() => import('@/pages/ImbalanceSettings'));

const CapacitySettings = React.lazy(() => import('@/pages/CapacitySettings'));
const PonGasPriceSettings = React.lazy(
  () => import('@/pages/PonGasPriceSettings'),
);
const PsoFormulaParametersSettings = React.lazy(
  () => import('@/pages/PsoFormulaParametersSettings'),
);
const MarketGasPriceTypesSettings = React.lazy(
  () => import('@/pages/MarketGasPriceTypesSettings'),
);
const PsoFormulaVariablesSettings = React.lazy(
  () => import('@/pages/PsoFormulaVariablesSettings'),
);

export const routes: AppRouteObject[] = [
  {
    path: OrganizationPages.ROLES_PART,
    element: (
      <OrganizationApiContextWrapper>
        <Outlet />
      </OrganizationApiContextWrapper>
    ),
    children: rolesChildren(false),
  },
  {
    element: (
      <SupplyOrganizationApiWrapper>
        <MyOrganization
          employeesPath={Pages.EMPLOYEES_PART}
          bankDetailsPath={Pages.BANK_DETAILS_PART}
        />
      </SupplyOrganizationApiWrapper>
    ),
    children: [
      { index: true, element: <MyOrganizationInformation /> },
      {
        path: Pages.EMPLOYEES_PART,
        element: <MyOrganizationEmployees />,
      },
      {
        path: Pages.BANK_DETAILS_PART,
        element: <MyOrganizationBankDetails />,
      },
    ],
  },
  {
    path: `${Pages.EMPLOYEES_PART}/:employeeId`,
    element: (
      <SupplyOrganizationApiWrapper>
        <EmployeeDetails />
      </SupplyOrganizationApiWrapper>
    ),
  },
  {
    path: Pages.UNDISTRIBUTED_PAYMENTS,
    element: <Navigate to={dayjs().format(ISO_MONTH_FORMAT)} replace />,
    handle: {
      permissions: UserPermissions.OrganizationsUndistributedPaymentsRead,
    },
  },
  {
    path: `${Pages.UNDISTRIBUTED_PAYMENTS}/:date`,
    element: <UndistributedPayments />,
    handle: {
      permissions: UserPermissions.OrganizationsUndistributedPaymentsRead,
    },
  },
  {
    path: Pages.PAYMENT_IMPORTS,
    element: <PaymentImports />,
    handle: {
      permissions: UserPermissions.OrganizationsUndistributedPaymentsRead,
    },
  },
  {
    path: `${Pages.PAYMENT_IMPORTS}/:importId`,
    element: <PaymentImportDetails />,
    handle: {
      permissions: UserPermissions.OrganizationsUndistributedPaymentsRead,
    },
  },
  {
    path: Pages.COUNTERPARTIES,
    element: <Counterparties />,
    handle: {
      permissions: UserPermissions.OrganizationsCounterpartiesRead,
    },
  },
  {
    path: `${Pages.COUNTERPARTIES}/${Pages.COUNTERPARTY_DETAILS}`,
    element: <CounterpartyDetails />,
    children: counterpartyDetailRoutes,
    handle: {
      permissions: UserPermissions.OrganizationsCounterpartiesRead,
    },
  },
  {
    path: Pages.GAS_TARIFFS,
    element: <Navigate to={dayjs().format(ISO_MONTH_FORMAT)} replace />,
    handle: {
      permissions: UserPermissions.OrganizationsPricesAndTariffsRead,
    },
  },
  {
    path: `${Pages.GAS_TARIFFS}/:date`,
    element: <GasTariffs />,
    children: gasTariffsDetailRoutes,
    handle: {
      permissions: UserPermissions.OrganizationsPricesAndTariffsRead,
    },
  },
  {
    path: Pages.RESOURCES,
    element: <Navigate to={dayjs().format(ISO_MONTH_FORMAT)} replace />,
    handle: {
      permissions: UserPermissions.GasBalanceResourceRead,
    },
  },
  {
    path: `${Pages.RESOURCES}/:date`,
    element: <Resources />,
    children: resourcesDetailRoutes,
    handle: {
      permissions: UserPermissions.GasBalanceResourceRead,
    },
  },
  {
    path: `${Pages.RESOURCES}/:date/${Pages.RESOURCE_DETAILS}`,
    element: <ResourceDetails />,
    children: resourceDetailRoutes,
    handle: {
      permissions: UserPermissions.GasBalanceResourceRead,
    },
  },
  {
    path: Pages.INCOMING_PAYMENTS,
    element: <Navigate to={dayjs().format(ISO_MONTH_FORMAT)} replace />,
    handle: {
      permissions: UserPermissions.OrganizationsIncomingPaymentsRead,
    },
  },
  {
    path: `${Pages.INCOMING_PAYMENTS}/:date`,
    element: <IncomingPayments />,
    handle: {
      permissions: UserPermissions.OrganizationsIncomingPaymentsRead,
    },
  },
  {
    path: `${Pages.INCOMING_PAYMENTS}/:date/${Pages.PAYMENT_DETAILS}`,
    element: <IncomingPaymentDetails />,
    handle: {
      permissions: UserPermissions.OrganizationsIncomingPaymentsRead,
    },
  },
  {
    path: Pages.CONTRACTS,
    element: <Contracts />,
    handle: {
      permissions: UserPermissions.OrganizationsContractsRead,
    },
  },
  {
    path: Pages.ADVANCE_INVOICES,
    element: <AdvanceInvoices />,
    children: advanceInvoicesRoutes,
    handle: {
      permissions: UserPermissions.OrganizationsActsAndInvoicesRead,
    },
  },
  {
    path: `${Pages.CONTRACTS}/:contractId`,
    element: <ContractDetails />,
    children: contractDetailsRoutes,
    handle: {
      permissions: UserPermissions.OrganizationsContractsRead,
    },
  },
  {
    path: Pages.REPORTS_LIST,
    element: <Navigate to='all' replace />,
    handle: {
      permissions: UserPermissions.ReportsAndAnalyticsReportRead,
    },
  },
  {
    path: `${Pages.REPORTS_LIST}/:reportTab`,
    element: <Reports />,
    handle: {
      permissions: UserPermissions.ReportsAndAnalyticsReportRead,
    },
  },
  {
    path: `${Pages.REPORTS}/constructor`,
    element: <ReportConstructor />,
  },
  {
    path: `${Pages.REPORTS}/constructor/:reportTemplateId`,
    element: <ReportConstructor />,
  },
  {
    path: Pages.AUDIT,
    element: <Audit />,
    handle: {
      permissions: UserPermissions.ReportsAndAnalyticsReportRead,
    },
  },
  {
    path: Pages.BI_DASHBOARDS,
    element: <BiDashboards />,
    handle: {
      permissions: UserPermissions.ReportsAndAnalyticsBiDashboardsRead,
    },
  },
  {
    path: PathBuilder.page(Pages.IMBALANCE_SETTINGS).build(),
    element: <ImbalanceSettings />,
    handle: {
      permissions: UserPermissions.SettingsImbalanceSettingsRead,
    },
  },
  {
    path: PathBuilder.page(Pages.CAPACITY_SETTINGS).build(),
    element: <CapacitySettings />,
    handle: {
      permissions: UserPermissions.SettingsCapacitySettingsRead,
    },
  },
  {
    path: PathBuilder.page(Pages.PON_GAS_PRICE_SETTINGS).build(),
    element: <PonGasPriceSettings />,
    handle: {
      permissions: UserPermissions.SettingsPsoFormulaRead,
    },
  },
  /** Формула ПОН */
  {
    path: PathBuilder.page(Pages.PSO_FORMULA_PARAMETERS).build(),
    element: <PsoFormulaParametersSettings />,
    handle: {
      permissions: UserPermissions.SettingsPsoFormulaRead,
      enabled: Config.VITE_FEATURE_FLAG_PSO_PRICES_FORMULA_SETTINGS,
    },
  },
  /** Формула ПОН Параметри */
  {
    path: PathBuilder.page(Pages.MARKET_GAS_PRICE_TYPES).build(),
    element: <MarketGasPriceTypesSettings />,
    handle: {
      permissions: UserPermissions.SettingsPsoFormulaRead,
      enabled: Config.VITE_FEATURE_FLAG_PSO_PRICES_FORMULA_SETTINGS,
    },
  },
  /** Формула ПОН Змінні */
  {
    path: PathBuilder.page(Pages.PSO_FORMULA_VARIABLES).build(),
    element: <PsoFormulaVariablesSettings />,
    handle: {
      permissions: UserPermissions.SettingsPsoFormulaRead,
      enabled: Config.VITE_FEATURE_FLAG_PSO_PRICES_FORMULA_SETTINGS,
    },
  },
  { path: '*', element: <NotFound /> },
  { path: Pages.NOT_FOUND, element: <NotFound /> },
];

export const allRoutes = [
  {
    path: '/',
    element: <AuthLanding />,
  },
  {
    // Kept for old bookmarks: the OIDC callback itself now lives on the BFF
    // (/signin-oidc), this is just the same landing page.
    path: Pages.AUTH_CALLBACK,
    element: <AuthLanding />,
  },
  {
    path: Pages.NO_PERMISSIONS,
    element: <NoPermissions />,
  },
  {
    path: Pages.NOT_AUTHORIZED,
    element: <NotAuthorizedPage />,
  },
  {
    path: '/:contextOrganizationId/*',
    element: <Router />,
    children: routes,
  },
  { path: Pages.NOT_FOUND, element: <NotFound /> },
  { path: '*', element: <NotFound /> },
];

export const getRoutes = () =>
  createBrowserRouter(
    allRoutes.map(route => ({ ...route, errorElement: <Error /> })),
  );
